import { API_BASE_URL } from "../../../shared";
import { blockchainfeatures } from "../../../services";
import { authApi } from "../api";
import { CODE_LENGTH, EMAIL_REGEX } from "../config";
import { validationError } from "./serviceError";

// Sign-in flows for the User block.
//
// Framework-free on purpose: no React, no toast, no navigate. Each function
// returns a result or throws an Error carrying the message the caller should
// show, so the UI decides how to present it and these stay testable without a DOM.

/**
 * Send a one-time login code to an email address. Also backs "Resend code".
 *
 * @param {string} email
 * @returns {Promise<string>} the trimmed email the code was sent to
 * @throws {Error} when the address is missing or malformed, before any request
 */
export const requestLoginCode = async (email) => {
  const trimmed = (email || "").trim();

  if (!trimmed) throw validationError("⚠️ Please enter your email.", "warn");
  if (!EMAIL_REGEX.test(trimmed))
    throw validationError("⚠️ Please enter a valid email.", "warn");

  await authApi.requestEmailCode(trimmed);
  return trimmed;
};

/**
 * Exchange a one-time code for a session cookie.
 *
 * @param {string} email
 * @param {string[]|string} code the box values, or the digits already joined
 * @returns {Promise<{success?: boolean, message?: string}>}
 * @throws {Error} when the code is incomplete, before any request
 */
export const verifyLoginCode = async (email, code) => {
  const digits = (Array.isArray(code) ? code.join("") : code || "").replace(
    /\D/g,
    "",
  );

  if (digits.length < CODE_LENGTH)
    throw validationError(`Enter the ${CODE_LENGTH}-digit code.`, "warn");

  return authApi.verifyEmailCode(email, digits);
};

/** Trade a Google credential for a session cookie. */
export const signInWithGoogle = (credential) =>
  authApi.googleSignIn(credential);

/**
 * The full MetaMask sign-in: connect, sign the challenge, post the signature.
 *
 * Returns a status instead of showing anything, so every branch the caller has
 * to handle is explicit and this function stays free of React:
 *
 *   { status: "redirecting", url }    mobile without the MetaMask browser
 *   { status: "no-wallet" }           window.ethereum missing
 *   { status: "not-connected" }       the connect prompt was dismissed
 *   { status: "signed-in", address }  connected, signed and posted
 *
 * This is the only place in the block that reaches into
 * src/services/blockchain/walletService - that code belongs to the future Wallet
 * block, so no component imports it directly.
 *
 * @returns {Promise<{status: string, url?: string, address?: string}>}
 */
export const signInWithWallet = async () => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const isMetaMaskBrowser = window.ethereum && window.ethereum.isMetaMask;

  // A mobile browser cannot reach the extension, so hand off to the MetaMask
  // app's in-app browser rather than failing with "no wallet found".
  if (isMobile && !isMetaMaskBrowser) {
    const currentUrl = window.location.href.replace(/^https?:\/\//, "");
    return {
      status: "redirecting",
      url: `https://metamask.app.link/dapp/${currentUrl}`,
    };
  }

  if (!window.ethereum) return { status: "no-wallet" };

  const { provider, address } = await blockchainfeatures.connectWallet();
  if (!address) return { status: "not-connected" };

  await blockchainfeatures.signMessageAndSend({
    provider,
    address,
    endpoint: `${API_BASE_URL}${authApi.WEB3_AUTH_ENDPOINT}`,
  });

  return { status: "signed-in", address };
};

// Turns a rejected reset request into one displayable line.
//
// A 422 carries FastAPI's field-level validation array, which has to be
// flattened; anything else carries a single message.
const resetErrorMessage = (error) => {
  if (!error.response) {
    return error.request
      ? "No response from the server"
      : "Unexpected error occurred";
  }

  const { status, data } = error.response;

  if (status === 422) {
    const details = data?.detail;
    if (Array.isArray(details)) {
      return details
        .map((item) => `${item.loc.join(".")}: ${item.msg}`)
        .join(", ");
    }
    return details || "Validation error";
  }

  return data?.message || data?.detail || "Server error occurred";
};

/**
 * Set a new password using the token from the reset email.
 *
 * @param {string} token
 * @param {string} password
 * @param {string} confirmation
 * @throws {Error} on a mismatch, a weak password, or a rejected request
 */
export const completePasswordReset = async (token, password, confirmation) => {
  if (password !== confirmation) {
    throw new Error(
      "The passwords do not match. Please make sure both fields are identical.",
    );
  }

  const isStrongEnough =
    password.length >= 8 && /[a-zA-Z]/.test(password) && /\d/.test(password);

  if (!isStrongEnough) {
    throw new Error(
      "Password must be at least 8 characters long and contain at least one letter and one digit.",
    );
  }

  try {
    return await authApi.resetPassword(token, password);
  } catch (error) {
    console.error("Reset password error:", error);
    throw new Error(resetErrorMessage(error));
  }
};

/** Invalidate the server session. Never throws. */
export const logout = () => authApi.logout();
